/**
 * @page PreiskalkulatorAnfragen
 * @route /preiskalkulator/anfragen
 * @description Preisanfragen — Leads aus dem Preiskalkulator.
 *   KPI-Cards, sortierbare Tabelle mit Volumen/Preis aus meta, CSV-Export, Pagination.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ClipboardList, Download, ExternalLink, Package, TrendingUp, BarChart3, Calculator, ChevronLeft, ChevronRight } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const apiFetch = (path) =>
  fetch(`${API}${path}`, { credentials: 'include' }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });

const STAGE_COLORS = {
  preisanfrage: 'bg-cyan-50 text-cyan-700',
  neu: 'bg-amber-50 text-amber-700',
  kontaktiert: 'bg-blue-50 text-blue-700',
  termin_vereinbart: 'bg-sky-50 text-sky-700',
  angebot_gesendet: 'bg-indigo-50 text-indigo-700',
  gewonnen: 'bg-emerald-50 text-emerald-700',
  verloren: 'bg-gray-50 text-[var(--text-secondary,#4B5563)]',
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtPrice = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '—' : `€${n.toFixed(2).replace('.', ',')}`;
};

const fmtVol = (v) => {
  const n = parseInt(v);
  return isNaN(n) ? '—' : n.toLocaleString('de-DE');
};

export default function PreiskalkulatorAnfragen() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, stats: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    const d = await apiFetch(`/api/preiskalkulator/leads?page=${page}&limit=${limit}`);
    setData(d);
    setLoading(false);
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    window.open(`${API}/api/preiskalkulator/leads/export`, '_blank');
  };

  // Client-side sort
  const sorted = [...(data.items || [])].sort((a, b) => {
    let va, vb;
    if (sortField === 'created_at') {
      va = new Date(a.created_at).getTime();
      vb = new Date(b.created_at).getTime();
    } else if (sortField === 'volume') {
      va = parseInt(a.meta?.monthlyPackages) || 0;
      vb = parseInt(b.meta?.monthlyPackages) || 0;
    } else if (sortField === 'price') {
      va = parseFloat(a.meta?.totalPerPackage) || 0;
      vb = parseFloat(b.meta?.totalPerPackage) || 0;
    } else {
      return 0;
    }
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-[var(--text-muted)] ml-0.5">↕</span>;
    return <span className="text-[var(--primary)] ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const stats = data.stats || {};

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[var(--primary-light,#EBF4FD)] flex items-center justify-center">
            <ClipboardList size={18} className="text-[var(--primary,#338edc)]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-[var(--text-primary)]">{t('calcLeads.title')}</h1>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--primary-light)] text-[var(--primary)]">
                {data.total}
              </span>
            </div>
            <p className="text-xs text-[var(--text-secondary)]">{t('calcLeads.subtitle')}</p>
          </div>
        </div>
        <button onClick={handleExport}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--border-default,#E5E7EB)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
          <Download size={13} /> {t('calcLeads.export')}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={t('calcLeads.total')} value={stats.gesamt || 0} icon={ClipboardList} iconColor="text-[#338edc]" />
        <KpiCard label={t('calcLeads.thisWeek')} value={stats.diese_woche || 0} icon={TrendingUp} iconColor="text-[#10B981]" />
        <KpiCard label={t('calcLeads.converted')} value={stats.konvertiert || 0} icon={BarChart3} iconColor="text-[#6366F1]" />
        <KpiCard label={t('calcLeads.avgVolume')} value={stats.avg_volumen ? Math.round(parseFloat(stats.avg_volumen)).toLocaleString('de-DE') : '0'} icon={Package} iconColor="text-[#22D3EE]" sub={t('calcLeads.packagesMonth')} />
      </div>

      {/* Table */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
                <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcLeads.company')}</th>
                <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcLeads.name')}</th>
                <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcLeads.email')}</th>
                <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcLeads.phone')}</th>
                <th className="text-right px-3 py-2 text-[var(--text-secondary)] font-medium cursor-pointer select-none" onClick={() => toggleSort('volume')}>
                  {t('calcLeads.volume')}<SortIcon field="volume" />
                </th>
                <th className="text-right px-3 py-2 text-[var(--text-secondary)] font-medium cursor-pointer select-none" onClick={() => toggleSort('price')}>
                  {t('calcLeads.pricePerPackage')}<SortIcon field="price" />
                </th>
                <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                  {t('calcLeads.created')}<SortIcon field="created_at" />
                </th>
                <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcLeads.stage')}</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--text-tertiary)]">{t('common.loading') || 'Loading...'}</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--text-tertiary)]">{t('calcLeads.noInquiries')}</td></tr>
              ) : sorted.map(lead => {
                const meta = lead.meta || {};
                const stageClass = STAGE_COLORS[lead.stage] || 'bg-gray-50 text-[var(--text-secondary,#4B5563)]';
                return (
                  <tr key={lead.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                    <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{lead.firma || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-primary)]">{lead.ansprechpartner || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)]">
                      {lead.email ? <a href={`mailto:${lead.email}`} className="text-[var(--primary)] hover:underline">{lead.email}</a> : '—'}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-secondary)]">{lead.telefon || '—'}</td>
                    <td className="px-3 py-2 text-right text-[var(--text-primary)] font-medium">{fmtVol(meta.monthlyPackages)}</td>
                    <td className="px-3 py-2 text-right text-[var(--text-primary)]">{fmtPrice(meta.totalPerPackage)}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)]">{fmtDate(lead.created_at)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${stageClass}`}>
                        {t(`sales.stages.${lead.stage}`) || lead.stage}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => navigate(`/sales/leads/${lead.id}`)}
                        className="flex items-center gap-1 text-[var(--primary)] hover:underline text-[11px] font-medium"
                      >
                        <ExternalLink size={12} /> {t('calcLeads.openLead')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <span>{t('calcLeads.showing')} {(page - 1) * limit + 1}–{Math.min(page * limit, data.total)} {t('calcLeads.of')} {data.total}</span>
            <select value={limit} onChange={e => { setLimit(parseInt(e.target.value)); setPage(1); }}
              className="px-2 py-1 rounded border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-1.5 rounded border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-30">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(data.pages, 5) }, (_, i) => {
              const p = page <= 3 ? i + 1 : page + i - 2;
              if (p < 1 || p > data.pages) return null;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`min-w-[28px] py-1 rounded text-xs font-medium ${p === page
                    ? 'bg-[var(--primary)] text-white'
                    : 'border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages}
              className="p-1.5 rounded border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-30">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, sub, iconColor }) {
  return (
    <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-1">{label}</div>
          <div className="text-[22px] font-semibold text-[var(--text-primary)]">{value}</div>
          {sub && <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{sub}</div>}
        </div>
        {Icon && <Icon size={20} className={iconColor || "text-[var(--text-tertiary)]"} />}
      </div>
    </div>
  );
}
